import ActionBlock from './ActionBlock.tsx';
import AddActionDropdown from './AddActionDropdown.tsx';
import type { Action, ActionType, AliasContextEntry, ActionTypeInfo } from '../types.ts';
import { createDefaultAction } from '../utils/actionDefaults.ts';

interface NestedActionListProps {
    actions: Action[];
    mappingIndex: number;
    keyPrefix: string;
    aliasContext: AliasContextEntry[];
    availableActionTypes: ActionTypeInfo[];
    onUpdateActions: (actions: Action[]) => void;
    filterOutActionTypes?: ActionType[];
}

const NestedActionList = ({
    actions,
    mappingIndex,
    keyPrefix,
    aliasContext,
    availableActionTypes,
    onUpdateActions,
    filterOutActionTypes,
}: NestedActionListProps) => {
    const handleAddAction = (actionType: ActionType) => {
        onUpdateActions([...actions, createDefaultAction(actionType)]);
    };

    const handleUpdateAction = (index: number, updates: Partial<Action>) => {
        onUpdateActions(actions.map((a, i) => (i === index ? ({ ...a, ...updates } as Action) : a)));
    };

    const handleDeleteAction = (index: number) => {
        onUpdateActions(actions.filter((_, i) => i !== index));
    };

    const handleMoveAction = (fromIndex: number, toIndex: number) => {
        const newActions = [...actions];
        const [moved] = newActions.splice(fromIndex, 1);
        newActions.splice(toIndex, 0, moved);
        onUpdateActions(newActions);
    };

    return (
        <div className="flex flex-col gap-2 pl-3 border-l-2 border-line-weak">
            {actions.map((action, index) => (
                <ActionBlock
                    key={`${keyPrefix}-${index}`}
                    action={action}
                    index={index}
                    mappingIndex={mappingIndex}
                    actionId={`${keyPrefix}-${index}`}
                    depth={1}
                    aliasContext={aliasContext}
                    onUpdateAction={handleUpdateAction}
                    onDeleteAction={handleDeleteAction}
                    onMoveAction={handleMoveAction}
                />
            ))}
            <AddActionDropdown
                actionTypes={availableActionTypes}
                onAddAction={handleAddAction}
                variant="ghost"
                filterOut={filterOutActionTypes}
            />
        </div>
    );
};

export default NestedActionList;
